// src/resources/customerRfm/RfmCustomersTable.tsx
//
// Lista paginada da tela "Segmentos de valor" (spec §6.2 item 6). Tabela MUI de
// mão (não Datagrid do react-admin), igual a customerLifecycle/LifecycleCustomersTable —
// `/customer-rfm/customers` pagina no servidor com query plana, não `filter=`.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import {
  Box, Chip, CircularProgress, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { __t } from '../../i18n/t';
import { PERMISSIONS } from '../../constants';
import { formatCellphone, formatCpf, formatPartiallyHiddenCpf } from '../../utils';
import { RfmCustomer, RfmFilters, fetchRfmCustomers } from './api';
import { SEGMENT_META } from './segments';

type SortField = 'totalValue' | 'lastFillinAt' | 'fillinCount' | 'name';

interface Props {
  filters: RfmFilters;
  onSearchChange?: (search: string) => void;
  onTotalChange?: (total: number) => void;
}

const RfmCustomersTable: React.FC<Props> = ({ filters, onSearchChange, onTotalChange }) => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);

  const [rows, setRows] = useState<RfmCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('totalValue');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');
  // `searchInput` é só o feedback imediato do campo de texto — o termo que de fato
  // filtra vira `filters.search` (subido pro CustomerRfmPage via `onSearchChange`),
  // pra que a tabela, o SegmentPlaybook (desconto/pontuar/CSV) e o TagSegmentDialog
  // (etiquetar) sempre ajam sobre o MESMO recorte que a tela mostra.
  const [searchInput, setSearchInput] = useState(filters.search ?? '');

  const filtersKey = JSON.stringify(filters);

  // Volta pra primeira página quando qualquer filtro (incluindo busca) muda — senão
  // a página pedida pode não existir mais no recorte novo.
  useEffect(() => { setPage(0); }, [filtersKey]);

  // Debounce de 400ms, só propaga busca com 3+ caracteres (contrato do backend).
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange?.(searchInput.trim().length >= 3 ? searchInput.trim() : '');
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    fetchRfmCustomers(
      filters,
      { skip: page * perPage, take: perPage, orderBy: sortField, orderDir: sortDir },
    )
      .then(({ data, total: t }) => {
        if (canceled) return;
        setRows(data);
        setTotal(t);
        onTotalChange?.(t);
      })
      .catch(() => { if (!canceled) { setRows([]); setTotal(0); onTotalChange?.(0); } })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page, perPage, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'ASC' : 'DESC');
    }
  };

  const cpfCell = (cpf: string | null) => {
    if (!cpf) return '—';
    return isAdmin ? formatCpf(cpf) : (cpf.includes('*') ? cpf : formatPartiallyHiddenCpf(cpf));
  };

  // Calculado fora do JSX de propósito (mesmo gotcha do plugin de i18n documentado
  // em LifecycleCustomersTable.tsx: um `as` dentro de atributo de JSX quebra o build).
  const SortableHeader = ({ field, label, align, maxWidth }: { field: SortField; label: string; align?: 'right'; maxWidth?: number }) => {
    const active = sortField === field;
    const muiDir: 'asc' | 'desc' = sortDir === 'ASC' ? 'asc' : 'desc';
    const cellSortDirection: 'asc' | 'desc' | false = active ? muiDir : false;
    const labelDirection: 'asc' | 'desc' = active ? muiDir : 'asc';
    return (
      <TableCell sortDirection={cellSortDirection} align={align} sx={maxWidth ? { maxWidth } : undefined}>
        <TableSortLabel active={active} direction={labelDirection} onClick={() => handleSort(field)}>
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Buscar por nome ou CPF (3+ caracteres)"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        inputProps={{ maxLength: 60 }}
        sx={{ mb: 1, minWidth: 280, bgcolor: '#fff' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />
      {/* width 0 + minWidth 100%: ocupa a largura do card sem entrar no cálculo de largura do
          layout (o RaLayout-root do app tem min-width fit-content e a tabela de 10 colunas esticava
          a página inteira no mobile). A tabela rola na horizontal dentro do contêiner. */}
      <TableContainer sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
        {/* nowrap nas colunas curtas (CPF, celular, datas, valores): a tabela tem 10 colunas e,
            sem isso, CPF/celular quebravam em 2-3 linhas a 1440px. Rola na horizontal no mobile. */}
        <Table size="small" sx={{ '& td, & th': { fontSize: 12.5 }, '& .rfm-nowrap': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <SortableHeader field="name" label="Cliente" />
              <TableCell>Celular</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell align="center">💎</TableCell>
              <SortableHeader field="lastFillinAt" label="Último abastecimento" maxWidth={110} />
              <SortableHeader field="fillinCount" label="Vezes 12m" align="right" maxWidth={90} />
              <SortableHeader field="totalValue" label="Valor 12m" align="right" />
              <TableCell>Posto principal</TableCell>
              <TableCell>Etiquetas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">Nenhum cliente neste grupo agora.</Typography>
              </TableCell></TableRow>
            ) : rows.map((c) => {
              const meta = SEGMENT_META[c.segment];
              const prevMeta = c.previousSegment ? SEGMENT_META[c.previousSegment] : null;
              const changed = c.previousSegment !== c.segment;
              const visibleTags = (c.tags || []).slice(0, 2);
              const extraTags = (c.tags || []).length - visibleTags.length;
              return (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/customers/${c.id}/show`)}>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>{c.name}</Typography>
                    <Typography className="rfm-nowrap" sx={{ fontSize: 11, color: 'text.secondary' }}>{cpfCell(c.cpf)}</Typography>
                  </TableCell>
                  <TableCell className="rfm-nowrap">{c.cellphone ? formatCellphone(c.cellphone) : '—'}</TableCell>
                  <TableCell>
                    <Chip label={`${meta.emoji} ${__t(meta.label)}`} size="small" sx={{ bgcolor: `${meta.color}1f`, color: meta.color, fontWeight: 700 }} />
                    {changed && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.25 }}>
                        {/* i18n: template literal dentro de ternário não é embrulhado pelo
                            plugin de build — __t explícito nas duas pontas. */}
                        {__t('antes: %{segment}', { segment: prevMeta ? `${prevMeta.emoji} ${__t(prevMeta.label)}` : __t('novo') })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {c.highValue && (
                      <Tooltip title="Top 20% de gasto da rede" arrow><span>💎</span></Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="rfm-nowrap">há {c.daysSinceLast} {c.daysSinceLast === 1 ? 'dia' : 'dias'}</TableCell>
                  <TableCell align="right">{c.fillinCount.toLocaleString('pt-BR')}</TableCell>
                  <TableCell align="right" className="rfm-nowrap">{c.totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                  <TableCell sx={{ maxWidth: 130 }}>
                    <Tooltip title={c.mainPlaceName || ''} arrow>
                      <Typography noWrap sx={{ fontSize: 12.5 }}>{c.mainPlaceName || '—'}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 150 }} onClick={(e) => e.stopPropagation()}>
                    {visibleTags.map((t) => (
                      <Chip key={t.id} label={t.name} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: t.color || undefined }} />
                    ))}
                    {extraTags > 0 && <Chip label={`+${extraTags}`} size="small" variant="outlined" />}
                    {!c.tags?.length && '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={perPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Box>
  );
};

export default RfmCustomersTable;
